<script lang="ts" module>
	import type { MarkdownTokenList } from '../Markdown';
	/**
	 * `Line` is the type of line1/line2: plain text by default. The component
	 * itself also takes Markdown tokens parsed on the server
	 * (`ArtworkCardProps<string | MarkdownTokenList>`); a string with a `<br>`
	 * (or `variants.line2: 'markdown'`) is Markdown parsed where it renders.
	 */
	export interface ArtworkCardProps<Line = string> {
		alt: string;
		image: { url: string; width: number; height: number };
		/** Load the image eagerly, for a card that is above the fold. */
		priority?: boolean;
		line1: Line;
		line2?: Line;
		line3?: string;
		href?: string;
		class?: string;
		children?: import('svelte').Snippet;
		onClick?: () => void;
		dataCy: string;
		external?: boolean | undefined;
		whiteBorderFix?: boolean | undefined;
		variants?: {
			line1?: TxtVariant;
			line2?: TxtVariant | 'markdown';
			line3?: TxtVariant;
		};
		classes?: {
			container?: string;
			image?: string;
			imageBorder?: string;
			imageContainer?: string;
			info?: string;
			card?: string;
			line1?: string;
			line2?: string;
			line3?: string;
		};
		line2Snippet?: import('svelte').Snippet;
		line3Snippet?: import('svelte').Snippet;
	}
</script>

<script lang="ts">
	import { twMerge } from 'tailwind-merge';
	import { LazyMarkdown } from '../LazyMarkdown';
	import { Link } from '../Link';
	import { plainTextTokens } from '../Markdown/utils/plainTextTokens/plainTextTokens';
	import { Txt, type TxtVariant } from '../Txt';
	import { WhiteBorderImageContainer } from '../WhiteBorderImageContainer';

	let {
		dataCy,
		external = false,
		whiteBorderFix = true,
		href = undefined,
		alt,
		image,
		line1,
		line2 = undefined,
		line3 = undefined,
		onClick = undefined,
		priority = false,
		line2Snippet,
		line3Snippet,
		variants = {},
		classes = {},
		children,
		...rest
	}: ArtworkCardProps<string | MarkdownTokenList> = $props();

	// A title with only `<br>` for markup needs no parser: its tokens are built
	// here. Anything else is parsed by LazyMarkdown.
	const asMarkdown = (line: string | MarkdownTokenList | undefined) =>
		typeof line === 'string' ? (plainTextTokens(line) ?? line) : line;

	// Whether the line has a `<br>`, which picks the multi-line style, read
	// from the tokens when the line was parsed on the server.
	const hasLineBreak = (line: string | MarkdownTokenList | undefined) =>
		typeof line === 'string' ? line.includes('<br>') : JSON.stringify(line ?? []).includes('<br>');

	let dataCyPrefix = $derived(`${dataCy}-artwork-card`);
</script>

<Link
	{href}
	class={twMerge(rest.class, classes.container)}
	data-cy={dataCyPrefix}
	{...external ? { rel: 'noopener noreferrer', target: '_blank' } : {}}
	onclick={onClick as () => void}
>
	<div class={twMerge('flex w-full flex-col transition-colors group h-full', classes.card)}>
		<div class={twMerge('h-[18.75rem] items-center justify-center', classes.imageContainer)}>
			<WhiteBorderImageContainer
				class={twMerge('h-[18.75rem] p-[1rem]', classes.image)}
				{image}
				{alt}
				loading={priority ? 'eager' : 'lazy'}
				{whiteBorderFix}
				dataCy={dataCyPrefix}
				classes={{
					border: classes.imageBorder
				}}
			/>
		</div>
		<div
			class={twMerge(
				'flex h-[calc(100%-18.75rem)] flex-col justify-between p-4 group-hover:bg-gray-50',
				classes.info
			)}
		>
			<div class="flex flex-col gap-1">
				{#if Array.isArray(line1) || hasLineBreak(line1)}
					<LazyMarkdown
						color="black"
						size="medium"
						source={asMarkdown(line1)}
						dataCy={`${dataCyPrefix}-line-1`}
						class={twMerge(
							'[&>p]:font-silka [&>p]:mb-0 [&>p]:text-[1rem] [&>p]:font-[500] [&>p]:leading-[1.25rem] [&>p]:tracking-[0] [&>p]:text-gray-900',
							classes.line1
						)}
					/>
				{:else}
					<Txt
						variant={variants.line1 || 'label2'}
						class={twMerge('text-gray-900', classes.line1)}
						dataCy={`${dataCyPrefix}-line-1`}
					>
						{line1}
					</Txt>
				{/if}

				{#if line2Snippet}
					{@render line2Snippet()}
				{:else if line2}
					{#if hasLineBreak(line2) || (Array.isArray(line2) && variants.line2 !== 'markdown')}
						<LazyMarkdown
							color="gray"
							size="medium"
							source={asMarkdown(line2)}
							dataCy={`${dataCyPrefix}-line-2`}
							class="[&>p]:mb-0 [&>p]:font-[500] [&>p]:leading-[1rem] [&>p]:tracking-[0] [&>p]:text-gray-700"
						/>
					{:else if variants.line2 === 'markdown'}
						<LazyMarkdown
							color="black"
							size="small"
							source={asMarkdown(line2)}
							dataCy={`${dataCyPrefix}-line-2`}
							class={twMerge('[&>p]:mb-0 [&>p]:leading-[1rem] [&>p]:tracking-[0]', classes.line2)}
						/>
					{:else}
						<Txt
							variant={variants.line2 || 'label3'}
							class={twMerge('text-gray-700', classes.line2)}
							dataCy={`${dataCyPrefix}-line-2`}
						>
							{line2}
						</Txt>
					{/if}
				{/if}

				{#if line3Snippet}
					{@render line3Snippet()}
				{:else if line3}
					<Txt
						variant={variants.line3 || 'label3'}
						class={twMerge('text-gray-700', classes.line3)}
						dataCy={`${dataCyPrefix}-line-3`}
					>
						{line3}
					</Txt>
				{/if}
			</div>

			{@render children?.()}
		</div>
	</div>
</Link>
